import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { BaseUpdateComponent } from '@app/shared/components/base/base-update.component';
import { FileUtilityService } from '@app/core/services/file-utility.service';
import { ContentStoreService } from '@app/core/services/content-store.service';
import { Office } from '@app/shared/models/office';
import { OfficeService, OfficeUriBuilderParams } from '@app/core/services/office.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { UntypedFormControl, Validators } from '@angular/forms';
import { FileType, FileTypes } from '@app/shared/models/file-type';
import { DrFileMeta, FileUploadStatus } from '@app/shared/models/dr-file';
import { ClientMediaType, MediaContainer } from '@app/shared/models/media-container';
import { FileActions } from '@app/shared/models/file-actions';
import { Client } from '@app/shared/models/client';

@Component({
    selector: 'smp-office',
    templateUrl: './office.component.html',
    styleUrls: ['./office.component.scss']
})
export class OfficeComponent extends BaseUpdateComponent<Office, OfficeUriBuilderParams>
                             implements OnInit {

    @Input()
    private client: Client;

    @Output()
    public canceled = new EventEmitter();

    protected paramsIdNames = [];

    public office_code: UntypedFormControl;
    public signatureAndStampFileTypes: FileType[];
    public officeMediaContainer: MediaContainer;
    public disabledImageOptions: FileActions[];

    public tempFiles: { file: File, drFileMeta: DrFileMeta }[];

    get office() {
        return this.updated;
    }

    get ceo(): string {
        return this.office && this.office.ceo && this.office.ceo.length > 0 ? this.office.ceo[0] : '';
    }

    set ceo(ceo: string) {
        this.office.ceo[0] = ceo;
    }

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected officeService: OfficeService,
                protected fileUtilityService: FileUtilityService,
                protected contentStoreService: ContentStoreService) {
        super(componentServiceBundle, officeService);
        this.signatureAndStampFileTypes = FileTypes.TYPES_PHOTO.concat(FileTypes.TYPES_IMAGE);
        this.disabledImageOptions = [FileActions.DOWNLOAD, FileActions.VIEW, FileActions.EDIT, FileActions.TOOLTIP_ON_HOVER];
    }

    public ngOnInit() {
        super.ngOnInit();

        this.office_code = new UntypedFormControl(
            {
                value: this.office.officeCode,
                disabled: this.disabled
            },
            {
                updateOn: 'blur',
                validators: [Validators.required],
            });
    }

    protected whenOriginalFetched(): void {
        if (this.office.id) {
            this.contentStoreService.getOfficeMediaFiles(this.office)
                                    .subscribe(result => {
                    this.officeMediaContainer = result;
                }, err => this.handleError(err));
        } else {
            this.officeMediaContainer = new MediaContainer();
        }
    }

    protected generateUriBuilderParams(): OfficeUriBuilderParams {
        return new OfficeUriBuilderParams(this.client, null, this.original);
    }

    // VALIDATION

    private valid(): boolean {
        this.office_code.markAsTouched();
        return this.office_code.valid;
    }

    public formToModel(): void {
        this.office.officeCode = this.office_code.value;
    }

    public getOfficeCodeError(): string {
        return this.office_code.hasError('required') ? marker('user_management.error.office_code.required') :
               '';
    }

    // IMAGES: LOGOS AND META GRAPHICS *********************************************************************************
    public onLogoFilesSelected(files: File[]): void {
        this.uploadMediaFile(files, ClientMediaType.LOGO);
    }

    public onMetaGraphicFilesSelected(files: File[]): void {
        this.uploadMediaFile(files, ClientMediaType.META_GRAPHIC);
    }

    protected uploadMediaFile(files: File[], type: ClientMediaType) {
        if (files) {
            files.forEach(file => {
                const newFileMeta = new DrFileMeta();
                newFileMeta.filename = file.name;
                newFileMeta.status = FileUploadStatus.LOADING;

                this.officeMediaContainer.addFile(newFileMeta, type);

                if (this.office.id) {
                    this.fileToContentStore(newFileMeta, file, type);
                } else {
                    if (!this.tempFiles) {
                        this.tempFiles = [];
                    }

                    this.tempFiles.push({ file: file, drFileMeta: newFileMeta });
                }
            });
        }
    }

    public deleteLogo(metaFile: DrFileMeta): void {
        this.deleteUserMediaFile(metaFile, ClientMediaType.LOGO);
    }

    public deleteMetaGraphic(metaFile: DrFileMeta): void {
        this.deleteUserMediaFile(metaFile, ClientMediaType.META_GRAPHIC);
    }

    protected deleteUserMediaFile(fileMeta: DrFileMeta, type: ClientMediaType): void {
        if (fileMeta.id) {
            this.contentStoreService.deleteOfficeMediaFile(this.office, type, fileMeta)
                .subscribe(result => {
                    this.officeMediaContainer.removeFile(fileMeta, type);
                }, err => this.handleError(err));
        } else {
            this.officeMediaContainer.removeFile(fileMeta, type);
        }
    }

    protected fileToContentStore(metaFile: DrFileMeta, file: File, type: ClientMediaType): void {
        this.fileUtilityService.fileAsBinaryString(file)
            .subscribe((fileAsBinaryString) => {
                this.contentStoreService.addOfficeMediaFile(this.office, type, fileAsBinaryString, metaFile)
                    .subscribe((result: DrFileMeta) => {
                            result.status = FileUploadStatus.READY;
                            this.officeMediaContainer.replaceFile(result, type);
                        },
                        err => {
                            metaFile.status = FileUploadStatus.ERROR;
                            this.officeMediaContainer.replaceFile(metaFile, type);
                        }
                    );
            });
    }


    // MAIN BUTTONS ****************************************************************************************************

    public cancel(): void {
        this.canceled.emit();
    }

    public submit(): void {
        if (this.valid()) {
            this.office.address.copyPostalCodes();

            if (this.office.id) {
                this.update();
            } else {
                this.createNewOffice();
            }

        }
    }

    protected createNewOffice(): void {
        this.formToModel();
        this.officeService.create(this.generateUriBuilderParams(), this.updated)
                          .subscribe(result => {
                this.reInitOriginalAndUpdated(result);
                this.saveTempFiles();
                this.bananaBoxChange.emit(this.original);
            }, err => this.handleError(err));
    }

    protected saveTempFiles(): void {
        this.officeMediaContainer.logo.entries.forEach(logoFileMeta => {
            const origFile = this.tempFiles.find(tmp => tmp.drFileMeta.order === logoFileMeta.order && tmp.drFileMeta.filename === logoFileMeta.filename);

            if (origFile) {
                this.fileToContentStore(origFile.drFileMeta, origFile.file, ClientMediaType.LOGO);
            }
        });

        this.officeMediaContainer.metagraphic.entries.forEach(metaGraphicFileMeta => {
            const origFile = this.tempFiles.find(tmp => tmp.drFileMeta.order === metaGraphicFileMeta.order && tmp.drFileMeta.filename === metaGraphicFileMeta.filename);

            if (origFile) {
                this.fileToContentStore(origFile.drFileMeta, origFile.file, ClientMediaType.META_GRAPHIC);
            }
        });

        this.tempFiles = [];
    }
}
